import { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import DatePicker from './datePicker';
import { toast } from 'react-toastify';
import { ISO_SERVICE_INFORMATION } from './constants';
import { Button } from 'react-bootstrap';
import { addDateWithYears } from './date';

/**
 * EquipmentMaintenanceFields is a React functional component for managing and displaying
 * equipment maintenance-related fields, including service information, periodicity, last service date,
 * and next service date. It provides logic to calculate the next service date based on user input,
 * validates input values, and updates ISO data accordingly.
 *
 * @component
 * @param {Object} props.isoData - Initial ISO data object containing maintenance field values.
 * @param {Object} props.ISOFields - Metadata and options for each ISO field.
 * @param {Function} props.updateISOData - Callback to update ISO data in the parent component.
 *
 * @example
 * <EquipmentMaintenanceFields
 *   isoData={isoData}
 *   ISOFields={ISOFields}
 *   updateISOData={handleUpdateISOData}
 * />
 */
export default function EquipmentMaintenanceFields(props) {
  const [ISOFields, setISOFields] = useState({});
  const [isoData, setIsoData] = useState(props.isoData || {});
  const [serviceInformation, setServiceInformation] = useState('');
  const [periodicity, setPeriodicity] = useState('');
  const [lastServiceDate, setLastServiceDate] = useState(null);
  const [nextServiceDate, setNextServiceDate] = useState(null);

  /**
   * Calculates and updates the next service date based on the given service date and periodicity value.
   *
   * The periodicity value can be a decimal number representing years (e.g., 1.5 for 1 year and 6 months).
   * The function computes the next service date, updates the relevant ISO data fields,
   * and performs validation to ensure the next service date is not in the past.
   *
   * If the calculated next service date is in the past, an error toast is shown and the next service date is cleared.
   * Otherwise, the next service date and ISO data are updated accordingly.
   *
   * @param {Date|string} serviceDate - The date of the last service. Can be a Date object or a date string.
   * @param {number|string} periodicityValue - The periodicity in years (can be decimal, e.g., 1.5).
   */
  const updateNextServiceDate = (serviceDate) => {
    let newISOData = isoData;
    Object.keys(newISOData).forEach((entity_dbid) => {
      const period = Number(
        newISOData[entity_dbid]?.Periodicity?.EntityMetaValue
      );
      let entityName = 'new equipment';
      props.entities?.forEach((entity) => {
        if (entity?.Entity_DBID === Number(entity_dbid)) {
          entityName = entity.EntityName || 'new equipment';
        }
      });
      if (!isNaN(period) && serviceDate) {
        const nextDate = addDateWithYears(new Date(serviceDate), period);
        // Check if the next service date is in the past
        if (nextDate < new Date()) {
          let toastMsg = 'Next service day ';
          toastMsg += 'for ' + entityName + ' ';
          toast.error(toastMsg + ' should be after current date');
          newISOData[entity_dbid].NextServiceDate = null;
          setNextServiceDate(null);
          updateISOData(newISOData, ISOFields['Last Service Date']);
          return;
        }

        // update ISO data for the respective entity
        newISOData[entity_dbid] = {
          ...newISOData[entity_dbid],
          Periodicity: {
            EntityMetaType_DBID: ISOFields['Periodicity'].EntityMetaType_DBID,
            EntityMetaValue: period,
          },
          NextServiceDate: {
            EntityMetaType_DBID:
              ISOFields['Next Service Date'].EntityMetaType_DBID,
            EntityMetaValue: nextDate,
          },
          LastServiceDate: {
            EntityMetaType_DBID:
              ISOFields['Last Service Date'].EntityMetaType_DBID,
            EntityMetaValue: serviceDate,
          },
        };

        setNextServiceDate(nextDate);
      } else {
        toast.error(
          'Periodicity or Last Service Date is invalid for ' + entityName
        );
        newISOData[entity_dbid].NextServiceDate = null;
        setNextServiceDate(null);
        updateISOData(newISOData, ISOFields['Last Service Date']);
        return;
      }
    });
    setIsoData(newISOData);
    updateISOData(newISOData, ISOFields['Last Service Date']);
    updateISOData(newISOData, ISOFields['Next Service Date']);
  };

  // Handle changes for service information
  const handleServiceInformationChange = (value) => {
    setServiceInformation(value);
    let newISOData = isoData;
    // update Service Information for all selected entities
    Object.keys(newISOData).forEach((entity_dbid) => {
      newISOData[entity_dbid] = {
        ...newISOData[entity_dbid],
        ServiceInformation: {
          EntityMetaType_DBID:
            ISOFields['Service Information'].EntityMetaType_DBID,
          EntityMetaValue: value ? value.toString() : '',
        },
      };
    });
    setIsoData(newISOData);
    updateISOData(newISOData, ISOFields['Service Information']);
  };

  // Handle changes for periodicity and next service date
  const handlePeriodicityChange = (value) => {
    setPeriodicity(value);
    let newISOData = isoData;
    Object.keys(newISOData).forEach((entity_dbid) => {
      newISOData[entity_dbid] = {
        ...newISOData[entity_dbid],
        Periodicity: {
          EntityMetaType_DBID: ISOFields['Periodicity'].EntityMetaType_DBID,
          EntityMetaValue: value ? value.toString() : '',
        },
      };
    });

    setIsoData(newISOData);
    updateISOData(newISOData, ISOFields['Periodicity']);
    updateNextServiceDate(lastServiceDate, value);
  };

  const updateISOData = (newISOData, isoField) => {
    if (props.editing) {
      props.updateISOData(newISOData, isoField);
      return;
    }
    props.updateISOData(newISOData);
  };

  // Handle changes for last service date and update next service date
  const handleLastServiceDateChange = (date) => {
    if (date !== null && date !== undefined && date !== '') {
      setLastServiceDate(date);
      updateNextServiceDate(date);
    } else {
      // remove Last Service Date and Next Service Date from ISO data if the field is cleared
      setLastServiceDate(null);
      setNextServiceDate(null);
      let newISOData = isoData;
      Object.keys(newISOData).forEach((entity_dbid) => {
        if (newISOData[entity_dbid]?.LastServiceDate) {
          delete newISOData[entity_dbid].LastServiceDate;
          delete newISOData[entity_dbid].NextServiceDate;
        }
      });
      setIsoData(newISOData);
      updateISOData(newISOData, ISOFields['Last Service Date']);
      updateISOData(newISOData, ISOFields['Next Service Date']);
    }
  };

  // Initialize ISOFields and ISO data from props
  useEffect(() => {
    if (props.isoData !== null && props.entities?.length === 1) {
      // set and display ISO data for single entity
      // for multiple entities, show empty fields
      let isoData = props.isoData[props.entities[0].Entity_DBID];
      if (isoData.ServiceInformation) {
        setServiceInformation(isoData.ServiceInformation.EntityMetaValue);
      }
      if (isoData.Periodicity) {
        setPeriodicity(isoData.Periodicity.EntityMetaValue);
      }
      if (isoData.LastServiceDate) {
        setLastServiceDate(new Date(isoData.LastServiceDate.EntityMetaValue));
      }
      if (isoData.NextServiceDate) {
        setNextServiceDate(new Date(isoData.NextServiceDate.EntityMetaValue));
      }
    }
    if (props.isoData === null) {
      let newISOData = {};
      props.entities?.forEach((entity) => {
        newISOData[entity.Entity_DBID] = {};
      });
      setIsoData(newISOData);
    }
    setISOFields(props.ISOFields || {});
  }, []);

  // Only render if ISOFields is not empty
  if (!ISOFields || Object.keys(ISOFields).length === 0) {
    return null;
  }

  return (
    <>
      <div>
        <div className="mb-2">
          <h6>
            Service Information{' '}
            {props.canEdit ? <span style={{ color: 'red' }}>*</span> : null}
          </h6>
          {props.canEdit ? (
            <Autocomplete
              className="type-ahead"
              options={ISOFields['Service Information']?.EntityMetaOption || []}
              getOptionLabel={(option) => option?.toString() || ''}
              onChange={(_event, newValue) => {
                handleServiceInformationChange(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select Service Information"
                />
              )}
              value={serviceInformation || ''}
            />
          ) : (
            <div className="w-100">
              <span className="disabled-meta">
                {props.isoData === null ? (
                  <br />
                ) : (
                  <>
                    {' '}
                    {serviceInformation ||
                      props.isoData?.[props.entities[0].Entity_DBID]
                        .ServiceInformation?.EntityMetaValue ||
                      ''}
                  </>
                )}
              </span>{' '}
            </div>
          )}
        </div>
        {props.entities?.length > 1 ? null : (
          <div className="mb-2">
            <h6>
              Periodicity (years){' '}
              <span
                style={{ color: 'red' }}
                hidden={
                  !props.canEdit ||
                  !serviceInformation ||
                  serviceInformation === ISO_SERVICE_INFORMATION.INDICATION
                }
              >
                *
              </span>
            </h6>
            {props.canEdit ? (
              <Autocomplete
                className="type-ahead"
                options={ISOFields['Periodicity']?.EntityMetaOption || []}
                getOptionLabel={(option) => option?.toString() || ''}
                onChange={(_event, newValue) => {
                  handlePeriodicityChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select Periodicity (years)"
                  />
                )}
                value={periodicity || ''}
                disabled={props.editing}
              />
            ) : (
              <div className="w-100">
                <span className="disabled-meta">
                  {props.isoData === null ? (
                    <br />
                  ) : (
                    <>
                      {periodicity ||
                        props.isoData?.[props.entities[0].Entity_DBID]
                          .Periodicity?.EntityMetaValue ||
                        ''}
                    </>
                  )}
                </span>{' '}
              </div>
            )}
          </div>
        )}
        <div className="mb-2">
          <h6>
            Last Service Date{' '}
            <span
              style={{ color: 'red' }}
              hidden={
                !props.canEdit ||
                !serviceInformation ||
                serviceInformation === ISO_SERVICE_INFORMATION.INDICATION
              }
            >
              *
            </span>
          </h6>
          {props.canEdit ? (
            <DatePicker
              renderInput={(props) => <TextField {...props} />}
              label="Event Last Service Date"
              update={(val) => handleLastServiceDateChange(val)}
              value={lastServiceDate || null}
            />
          ) : (
            <div className="w-100">
              <span className="disabled-meta">
                {lastServiceDate?.toLocaleDateString() || null}
              </span>{' '}
            </div>
          )}
        </div>
        <div className="mb-2">
          <br />
          {nextServiceDate && props.entities?.length === 1 ? (
            <div className="next-service-date">
              Next service date: {nextServiceDate?.toLocaleDateString()}
            </div>
          ) : null}
        </div>
      </div>
      {/* Service actions are visible only for single seleted equipment*/}
      {props.editing && props.entities?.length === 1 ? (
        <div className="service-footer">
          <Button
            variant="outline-primary"
            className="service-buttons"
            onClick={() => props.openServiceHistory(false)}
          >
            Service History
          </Button>
          <Button
            variant="outline-primary"
            className="service-buttons"
            onClick={() => props.handlePerformService(false)}
          >
            Perform Service
          </Button>
        </div>
      ) : null}
    </>
  );
}
